import React, { useEffect, useRef } from "react";
import { ArrowSquareOut } from "@phosphor-icons/react";

// Embedded TradingView Advanced Chart.
//
// IMPORTANT: TradingView's embed script must live INSIDE a `tradingview-widget-container`
// element, alongside the `tradingview-widget-container__widget` div. The script then
// injects the chart into the `__widget` div via its own bootstrap. Using `script.text`
// (not `innerHTML`) is the supported way to pass JSON config to that bootstrap.
export default function TradingViewWidget({ symbol = "NASDAQ:AAPL", interval = "60", height = 420 }) {
  const containerRef = useRef(null);

  useEffect(() => {
    const host = containerRef.current;
    if (!host) return;

    // Clear any prior widget DOM (previous symbol).
    host.innerHTML = "";

    const inner = document.createElement("div");
    inner.className = "tradingview-widget-container__widget";
    inner.style.height = "100%";
    inner.style.width = "100%";
    host.appendChild(inner);

    const script = document.createElement("script");
    script.src = "https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js";
    script.async = true;
    script.type = "text/javascript";
    script.text = JSON.stringify({
      autosize: true,
      symbol,
      interval,
      timezone: "Etc/UTC",
      theme: "dark",
      style: "1",
      locale: "en",
      backgroundColor: "#0A0A0A",
      gridColor: "rgba(255,255,255,0.05)",
      hide_side_toolbar: false,
      allow_symbol_change: true,
      studies: ["STD;RSI", "STD;MACD", "MASimple@tv-basicstudies"],
      calendar: false,
      withdateranges: true,
      support_host: "https://www.tradingview.com",
    });
    host.appendChild(script);

    return () => {
      if (host) host.innerHTML = "";
    };
  }, [symbol, interval]);

  return (
    <div className="bg-surface border border-white/10" data-testid="tradingview-widget">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <h3 className="heading text-base font-semibold">Live Chart</h3>
        <div className="flex items-center gap-3">
          <span className="overline" data-testid="tv-symbol">{symbol}</span>
          <a
            href={`https://www.tradingview.com/symbols/${encodeURIComponent(symbol)}/`}
            target="_blank"
            rel="noopener noreferrer"
            className="overline flex items-center gap-1 text-white/50 hover:text-white transition-colors"
            data-testid="tv-open-external"
            title="Open full chart on TradingView"
          >
            Open on TradingView
            <ArrowSquareOut size={11} weight="bold" />
          </a>
        </div>
      </div>
      <div
        ref={containerRef}
        style={{ height }}
        className="tradingview-widget-container"
      />
    </div>
  );
}
